// Contabilidad › Inteligencia de negocios — EDITOR DE FILAS (Fase 2).
// Cada fila es una "cuenta virtual": un nombre propio y una lista de TÉRMINOS
// con signo. Nunca se teclea un código: se elige del plan de cuentas y queda
// como un chip verde (+) o rojo (−) que se puede invertir con un clic.
// El valor de cada fila se recalcula en vivo contra el rango de fechas elegido,
// y el panel de cobertura avisa de las cuentas que faltan o están repetidas.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle, Check, ChevronDown, ChevronUp, Copy, Indent, Loader2, Outdent,
  Plus, Save, Trash2, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { BuscadorCuenta } from '@/components/ui/BuscadorCuenta';
import { getErrorMessage } from '@/lib/api/client';
import { Cuenta, cargarPlan, field, usd, rpc } from './shared';

export type TipoTermino = 'cuenta' | 'prefijo' | 'tipo' | 'fila';
export type Medida = 'flujo' | 'saldo';

export interface TerminoDef {
  signo: '+' | '-'; tipo: TipoTermino;
  /** código de cuenta, prefijo, tipo de cuenta, o la `key` de otra fila */
  valor: string;
  incluye_hijos: boolean;
}
export interface FilaDef {
  key: string; nivel: number; clase: string; etiqueta: string;
  medida: Medida; formato: string; negrita: boolean; terminos: TerminoDef[];
}
export interface Borrador {
  id: string | null; nombre: string; descripcion: string; base: string;
  columnas_tipo: string; columnas_param: string;
  mostrar_total: boolean; mostrar_variacion: boolean;
  fila_base_key: string | null;
  filas: FilaDef[];
}

const TIPOS_CUENTA: { v: string; label: string }[] = [
  { v: 'activo', label: 'Activos' }, { v: 'pasivo', label: 'Pasivos' },
  { v: 'patrimonio', label: 'Patrimonio' }, { v: 'ingreso', label: 'Ingresos' },
  { v: 'costo', label: 'Costos' }, { v: 'gasto', label: 'Gastos' },
];
const CLASES: { v: string; label: string }[] = [
  { v: 'grupo', label: 'Grupo' }, { v: 'subgrupo', label: 'Subgrupo' },
  { v: 'detalle', label: 'Detalle' }, { v: 'total', label: 'Total' },
  { v: 'separador', label: 'Separador' },
];
const UNIVERSOS: { v: string; label: string }[] = [
  { v: 'resultados', label: 'Cuentas de resultado (ingresos, costos, gastos)' },
  { v: 'balance', label: 'Cuentas de balance (activo, pasivo, patrimonio)' },
  { v: 'todo', label: 'Todo el plan de cuentas' },
];

export const nuevaKey = () => Math.random().toString(36).slice(2, 10);

export const filaVacia = (): FilaDef => ({
  key: nuevaKey(), nivel: 0, clase: 'detalle', etiqueta: '',
  medida: 'flujo', formato: 'moneda', negrita: false, terminos: [],
});

export const borradorVacio = (): Borrador => ({
  id: null, nombre: '', descripcion: '', base: 'resultados',
  columnas_tipo: 'tiempo', columnas_param: 'mensual',
  mostrar_total: true, mostrar_variacion: false, fila_base_key: null,
  filas: [{ ...filaVacia(), clase: 'grupo', etiqueta: 'Ingresos' }],
});

/** Filas en la forma que esperan las RPC: `orden` posicional y las referencias
 *  a otra fila resueltas de `key` a `orden`. */
export function filasParaRpc(filas: FilaDef[]) {
  const ordenDe = new Map(filas.map((f, i) => [f.key, (i + 1) * 10]));
  return filas.map((f, i) => ({
    orden: (i + 1) * 10, nivel: f.nivel, clase: f.clase, etiqueta: f.etiqueta.trim(),
    medida: f.medida, formato: f.formato, negrita: f.negrita,
    terminos: f.terminos.map((t, j) => ({
      orden: j + 1, signo: t.signo, tipo: t.tipo,
      valor: t.tipo === 'fila' ? String(ordenDe.get(t.valor) ?? 0) : t.valor.trim(),
      incluye_hijos: t.incluye_hijos,
    })),
  }));
}

/** Definición que devuelve `cont_bi_reporte` → borrador del editor. */
export function aBorrador(d: {
  id: string; nombre: string; descripcion: string | null; base: string;
  columnas_tipo: string; columnas_param: string;
  mostrar_total?: boolean; mostrar_variacion?: boolean; fila_base_orden?: number | null;
  filas: { orden: number; nivel: number; clase: string; etiqueta: string; medida: string; formato: string; negrita: boolean;
           terminos: { signo: string; tipo: string; valor: string; incluye_hijos: boolean }[] }[];
}, nuevoId: string | null = null): Borrador {
  const keyDe = new Map(d.filas.map((f) => [f.orden, nuevaKey()]));
  return {
    id: nuevoId ?? d.id,
    nombre: d.nombre, descripcion: d.descripcion ?? '', base: d.base,
    columnas_tipo: d.columnas_tipo, columnas_param: d.columnas_param,
    mostrar_total: d.mostrar_total ?? true,
    mostrar_variacion: d.mostrar_variacion ?? false,
    fila_base_key: d.fila_base_orden != null ? keyDe.get(d.fila_base_orden) ?? null : null,
    filas: d.filas.map((f) => ({
      key: keyDe.get(f.orden)!, nivel: f.nivel, clase: f.clase, etiqueta: f.etiqueta,
      medida: (f.medida === 'saldo' ? 'saldo' : 'flujo') as Medida,
      formato: f.formato, negrita: f.negrita,
      terminos: f.terminos.map((t) => ({
        signo: t.signo === '-' ? '-' : '+' as '+' | '-',
        tipo: t.tipo as TipoTermino,
        valor: t.tipo === 'fila' ? (keyDe.get(Number(t.valor)) ?? '') : t.valor,
        incluye_hijos: t.incluye_hijos,
      })),
    })),
  };
}

// ============================================================== CHIP ==========
function Chip({ t, filas, plan, onToggle, onQuitar, invalida }: {
  t: TerminoDef; filas: FilaDef[]; plan: Cuenta[];
  onToggle: () => void; onQuitar: () => void; invalida: boolean;
}) {
  const etq = t.tipo === 'fila'
    ? `«${filas.find((f) => f.key === t.valor)?.etiqueta || '?'}»`
    : t.tipo === 'tipo'
      ? (TIPOS_CUENTA.find((x) => x.v === t.valor)?.label ?? t.valor)
      : t.tipo === 'prefijo'
        ? `${t.valor}…`
        : `${t.valor}${t.incluye_hijos ? '*' : ''}`;
  const nombre = t.tipo === 'cuenta' || t.tipo === 'prefijo'
    ? plan.find((c) => c.codigo === t.valor)?.nombre : undefined;
  const color = invalida
    ? 'border-destructive/60 bg-destructive/10 text-destructive'
    : t.signo === '-'
      ? 'border-destructive/40 bg-destructive/5'
      : 'border-estable/40 bg-estable-soft';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${color}`}
      title={invalida ? 'Esta fila se referencia a sí misma o a una fila POSTERIOR' : nombre}>
      <button className="font-mono font-bold" onClick={onToggle} title="Cambiar el signo">{t.signo}</button>
      <span className="max-w-[16rem] truncate">{etq}</span>
      <button onClick={onQuitar} title="Quitar"><X className="h-3 w-3 opacity-60 hover:opacity-100" /></button>
    </span>
  );
}

// ====================================================== AGREGAR TÉRMINO =======
function AgregarTermino({ filas, idx, plan, onAgregar, onCerrar }: {
  filas: FilaDef[]; idx: number; plan: Cuenta[];
  onAgregar: (t: TerminoDef) => void; onCerrar: () => void;
}) {
  const [tipo, setTipo] = useState<TipoTermino>('cuenta');
  const [signo, setSigno] = useState<'+' | '-'>('+');
  const [valor, setValor] = useState('');
  const [hijos, setHijos] = useState(true);
  const anteriores = filas.slice(0, idx);

  const agregar = () => {
    if (!valor.trim()) { toast.error('Elige qué suma o resta este término'); return; }
    onAgregar({ signo, tipo, valor: valor.trim(), incluye_hijos: tipo === 'prefijo' ? true : hijos });
    setValor('');
  };

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 p-2">
      <div className="flex overflow-hidden rounded-md border">
        {(['+', '-'] as const).map((s) => (
          <button key={s} onClick={() => setSigno(s)}
            className={`px-2.5 py-1 font-mono text-sm ${signo === s ? 'bg-primary text-primary-foreground' : 'bg-background'}`}>{s}</button>
        ))}
      </div>
      <select className={`${field} w-36`} value={tipo}
        onChange={(e) => { setTipo(e.target.value as TipoTermino); setValor(''); }}>
        <option value="cuenta">Una cuenta</option>
        <option value="prefijo">Prefijo de código</option>
        <option value="tipo">Tipo de cuenta</option>
        <option value="fila">Otra fila</option>
      </select>

      {tipo === 'cuenta' && (
        <>
          <div className="w-72">
            <BuscadorCuenta compact allowClear soloMovimiento={false}
              value={plan.find((c) => c.codigo === valor)?.id ?? ''}
              onChange={(id) => setValor(plan.find((c) => c.id === id)?.codigo ?? '')} />
          </div>
          <label className="flex items-center gap-1 text-xs text-muted-foreground">
            <input type="checkbox" checked={hijos} onChange={(e) => setHijos(e.target.checked)} />
            incluir sus cuentas hijas
          </label>
        </>
      )}
      {tipo === 'prefijo' && (
        <input className={`${field} w-40`} placeholder="p. ej. 5.2" value={valor}
          onChange={(e) => setValor(e.target.value)} />
      )}
      {tipo === 'tipo' && (
        <select className={`${field} w-44`} value={valor} onChange={(e) => setValor(e.target.value)}>
          <option value="">— elige —</option>
          {TIPOS_CUENTA.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
        </select>
      )}
      {tipo === 'fila' && (
        <select className={`${field} w-64`} value={valor} onChange={(e) => setValor(e.target.value)}>
          <option value="">— elige una fila anterior —</option>
          {anteriores.map((f) => <option key={f.key} value={f.key}>{f.etiqueta || '(sin nombre)'}</option>)}
        </select>
      )}
      {tipo === 'fila' && anteriores.length === 0 && (
        <span className="text-xs text-muted-foreground">No hay filas anteriores a las que referirse.</span>
      )}

      <Button size="sm" onClick={agregar}><Plus className="mr-1 h-4 w-4" /> Agregar</Button>
      <Button size="sm" variant="ghost" onClick={onCerrar}>Listo</Button>
    </div>
  );
}

// ============================================================ COBERTURA =======
interface Cobertura {
  universo: string; total: number; cubiertas: number;
  faltan: { codigo: string; nombre: string; tipo: string; con_movimiento: boolean }[];
  repetidas: { codigo: string; nombre: string; filas: string[] }[];
  fuera_universo: { codigo: string; nombre: string; tipo: string; filas: string[] }[];
}

function PanelCobertura({ filas, desde, hasta }: { filas: FilaDef[]; desde: string; hasta: string }) {
  const [universo, setUniverso] = useState('resultados');
  const payload = useMemo(() => filasParaRpc(filas), [filas]);
  const { data, isFetching } = useQuery<Cobertura>({
    queryKey: ['bi-cobertura', universo, desde, hasta, JSON.stringify(payload)],
    queryFn: () => rpc<Cobertura>('cont_bi_cobertura', {
      _filas: payload, _universo: universo, _desde: desde, _hasta: hasta,
    }),
    enabled: filas.length > 0,
  });

  const faltanConMov = data?.faltan.filter((f) => f.con_movimiento) ?? [];
  const faltanSinMov = data?.faltan.filter((f) => !f.con_movimiento) ?? [];

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex flex-wrap items-center gap-3 text-base">
          Cobertura del plan de cuentas
          {isFetching && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
          <select className={`${field} ml-auto w-auto`} value={universo} onChange={(e) => setUniverso(e.target.value)}>
            {UNIVERSOS.map((u) => <option key={u.v} value={u.v}>{u.label}</option>)}
          </select>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-xs text-muted-foreground">
          Un reporte no cuadra contra el Estado de Resultados oficial por dos motivos: porque una
          cuenta quedó fuera de todas las filas, o porque está en dos filas a la vez.
        </p>

        {data && (
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-md px-2 py-1 text-xs ${data.cubiertas === data.total ? 'bg-estable-soft text-estable' : 'bg-muted'}`}>
              {data.cubiertas} de {data.total} cuentas cubiertas
            </span>
            {faltanConMov.length > 0 && (
              <span className="rounded-md bg-destructive/10 px-2 py-1 text-xs text-destructive">
                {faltanConMov.length} con saldo sin incluir
              </span>
            )}
            {data.repetidas.length > 0 && (
              <span className="rounded-md bg-amber-500/10 px-2 py-1 text-xs text-amber-600">
                {data.repetidas.length} en más de una fila
              </span>
            )}
            {data.fuera_universo.length > 0 && (
              <span className="rounded-md bg-amber-500/10 px-2 py-1 text-xs text-amber-600">
                {data.fuera_universo.length} fuera del universo elegido
              </span>
            )}
            {data.cubiertas === data.total && data.repetidas.length === 0 && data.fuera_universo.length === 0 && (
              <span className="inline-flex items-center gap-1 text-xs text-estable"><Check className="h-4 w-4" /> Sin huecos ni repeticiones</span>
            )}
          </div>
        )}

        {faltanConMov.length > 0 && (
          <div>
            <div className="mb-1 text-xs font-medium text-destructive">Faltan y TIENEN movimiento en el rango</div>
            <div className="flex flex-wrap gap-1">
              {faltanConMov.map((f) => (
                <span key={f.codigo} className="rounded border border-destructive/40 bg-destructive/5 px-2 py-0.5 text-xs" title={f.nombre}>
                  {f.codigo} · {f.nombre}
                </span>
              ))}
            </div>
          </div>
        )}
        {faltanSinMov.length > 0 && (
          <details>
            <summary className="cursor-pointer text-xs text-muted-foreground">
              {faltanSinMov.length} cuenta(s) sin incluir pero sin movimiento en el rango
            </summary>
            <div className="mt-1 flex flex-wrap gap-1">
              {faltanSinMov.map((f) => (
                <span key={f.codigo} className="rounded border px-2 py-0.5 text-xs text-muted-foreground" title={f.nombre}>
                  {f.codigo} · {f.nombre}
                </span>
              ))}
            </div>
          </details>
        )}
        {data && data.repetidas.length > 0 && (
          <div>
            <div className="mb-1 flex items-center gap-1 text-xs font-medium text-amber-600">
              <AlertTriangle className="h-4 w-4" /> En más de una fila (posible doble conteo)
            </div>
            <ul className="space-y-0.5 text-xs">
              {data.repetidas.map((r) => (
                <li key={r.codigo}><span className="font-mono">{r.codigo}</span> {r.nombre} → {r.filas.join(' · ')}</li>
              ))}
            </ul>
          </div>
        )}
        {data && data.fuera_universo.length > 0 && (
          <details>
            <summary className="cursor-pointer text-xs text-amber-600">
              {data.fuera_universo.length} cuenta(s) usadas que no son del universo elegido
            </summary>
            <ul className="mt-1 space-y-0.5 text-xs">
              {data.fuera_universo.slice(0, 40).map((r) => (
                <li key={r.codigo}><span className="font-mono">{r.codigo}</span> {r.nombre} ({r.tipo}) → {r.filas.join(' · ')}</li>
              ))}
            </ul>
          </details>
        )}
      </CardContent>
    </Card>
  );
}

// =============================================================== EDITOR =======
export function BIEditor({ borrador, setBorrador, desde, hasta, onGuardado, onCancelar }: {
  borrador: Borrador; setBorrador: (b: Borrador) => void;
  desde: string; hasta: string;
  onGuardado: (id: string) => void; onCancelar: () => void;
}) {
  const qc = useQueryClient();
  const [abierta, setAbierta] = useState<string | null>(null);
  const { data: plan = [] } = useQuery<Cuenta[]>({ queryKey: ['cont-plan'], queryFn: cargarPlan, staleTime: 300000 });

  const filas = borrador.filas;
  const set = useCallback((p: Partial<Borrador>) => setBorrador({ ...borrador, ...p }), [borrador, setBorrador]);
  const setFila = (i: number, p: Partial<FilaDef>) =>
    set({ filas: filas.map((f, j) => (j === i ? { ...f, ...p } : f)) });

  // Vista previa en vivo: una sola columna "Total" del rango, por fila.
  const [payload, setPayload] = useState(() => filasParaRpc(filas));
  useEffect(() => {
    const t = setTimeout(() => setPayload(filasParaRpc(filas)), 450);
    return () => clearTimeout(t);
  }, [filas]);

  const { data: previa, isFetching: calculando } = useQuery<{ filas: { orden: number; valores: number[] }[] }>({
    queryKey: ['bi-previa', desde, hasta, JSON.stringify(payload)],
    queryFn: () => rpc('cont_bi_previsualizar', {
      _filas: payload, _desde: desde, _hasta: hasta, _cols_tipo: 'tiempo', _cols_param: 'ninguno',
    }),
    enabled: payload.length > 0,
  });
  const valorDe = (i: number) => previa?.filas.find((f) => f.orden === (i + 1) * 10)?.valores?.[0];

  // Cuadre: una fila de grupo/subgrupo debe igualar la suma de sus hijas
  // inmediatas (las filas siguientes de un nivel más). Si no tiene hijas, no aplica.
  const cuadre = (i: number): number | null => {
    const f = filas[i];
    if (f.clase !== 'grupo' && f.clase !== 'subgrupo') return null;
    let suma = 0; let hijas = 0;
    for (let j = i + 1; j < filas.length; j++) {
      if (filas[j].nivel <= f.nivel) break;
      if (filas[j].nivel === f.nivel + 1) { suma += valorDe(j) ?? 0; hijas++; }
    }
    if (hijas === 0) return null;
    return Math.round(((valorDe(i) ?? 0) - suma) * 100) / 100;
  };

  const mover = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= filas.length) return;
    const n = [...filas]; [n[i], n[j]] = [n[j], n[i]]; set({ filas: n });
  };
  const quitarFila = (i: number) => {
    const key = filas[i].key;
    set({
      fila_base_key: borrador.fila_base_key === key ? null : borrador.fila_base_key,
      filas: filas.filter((_, j) => j !== i)
        // cae también cualquier referencia a la fila borrada
        .map((f) => ({ ...f, terminos: f.terminos.filter((t) => !(t.tipo === 'fila' && t.valor === key)) })),
    });
  };
  const agregarFila = (i: number) => {
    const n = [...filas]; n.splice(i + 1, 0, { ...filaVacia(), nivel: filas[i]?.nivel ?? 0 });
    set({ filas: n });
  };

  // Una referencia solo puede mirar hacia atrás
  const refInvalida = (i: number, t: TerminoDef) =>
    t.tipo === 'fila' && filas.findIndex((f) => f.key === t.valor) >= i;

  const guardar = useMutation({
    mutationFn: () => {
      if (!borrador.nombre.trim()) throw new Error('Ponle un nombre al reporte');
      if (filas.some((f) => !f.etiqueta.trim())) throw new Error('Hay filas sin nombre');
      if (filas.some((f, i) => f.terminos.some((t) => refInvalida(i, t)))) {
        throw new Error('Hay una referencia a una fila posterior: muévela o cámbiala');
      }
      const ordenDe = new Map(filas.map((f, i) => [f.key, (i + 1) * 10]));
      return rpc<{ id: string; nombre: string }>('cont_bi_guardar', {
        _def: {
          id: borrador.id, nombre: borrador.nombre.trim(), descripcion: borrador.descripcion,
          base: borrador.base, columnas_tipo: borrador.columnas_tipo, columnas_param: borrador.columnas_param,
          mostrar_total: borrador.mostrar_total, mostrar_variacion: borrador.mostrar_variacion,
          fila_base_orden: borrador.fila_base_key ? ordenDe.get(borrador.fila_base_key) ?? null : null,
          filas: filasParaRpc(filas),
        },
      });
    },
    onSuccess: (r) => {
      toast.success(`Reporte "${r.nombre}" guardado`);
      void qc.invalidateQueries({ queryKey: ['bi-reportes'] });
      void qc.invalidateQueries({ queryKey: ['bi-ejecutar'] });
      onGuardado(r.id);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-6">
          <label className="flex flex-col text-xs text-muted-foreground">Nombre del reporte
            <input className={`${field} mt-1 w-72`} value={borrador.nombre}
              onChange={(e) => set({ nombre: e.target.value })} placeholder="p. ej. P&G Gerencial 2026" />
          </label>
          <label className="flex flex-col text-xs text-muted-foreground">Descripción
            <input className={`${field} mt-1 w-80`} value={borrador.descripcion}
              onChange={(e) => set({ descripcion: e.target.value })} />
          </label>
          <label className="flex flex-col text-xs text-muted-foreground">Columnas por defecto
            <select className={`${field} mt-1 w-40`} value={borrador.columnas_tipo}
              onChange={(e) => set({ columnas_tipo: e.target.value })}>
              <option value="tiempo">Tiempo</option>
              <option value="centro">Centro de costo</option>
              <option value="proyecto">Proyecto</option>
              <option value="partner">Partner</option>
            </select>
          </label>
          {borrador.columnas_tipo === 'tiempo' && (
            <label className="flex flex-col text-xs text-muted-foreground">Período
              <select className={`${field} mt-1 w-40`} value={borrador.columnas_param}
                onChange={(e) => set({ columnas_param: e.target.value })}>
                <option value="anual">Anual</option>
                <option value="semestral">Semestral</option>
                <option value="trimestral">Trimestral</option>
                <option value="bimensual">Bimensual</option>
                <option value="mensual">Mensual</option>
                <option value="semanal">Semanal (operativo)</option>
                <option value="ninguno">Un solo total</option>
              </select>
            </label>
          )}
          <label className="flex flex-col text-xs text-muted-foreground">Fila base del análisis vertical
            <select className={`${field} mt-1 w-56`} value={borrador.fila_base_key ?? ''}
              onChange={(e) => set({ fila_base_key: e.target.value || null })}>
              <option value="">— ninguna —</option>
              {filas.map((f) => <option key={f.key} value={f.key}>{f.etiqueta || '(sin nombre)'}</option>)}
            </select>
          </label>
          <div className="ml-auto flex items-end gap-2 pb-1">
            <Button variant="outline" size="sm" onClick={onCancelar}>Cancelar</Button>
            <Button size="sm" disabled={guardar.isPending} onClick={() => guardar.mutate()}>
              {guardar.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
              Guardar
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            Filas ({filas.length})
            {calculando && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
            <span className="ml-auto text-xs font-normal text-muted-foreground">
              Valores del {desde} al {hasta}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {filas.map((f, i) => {
            const d = cuadre(i);
            const v = valorDe(i);
            return (
              <div key={f.key} className="rounded-md border p-2" style={{ marginLeft: `${f.nivel * 20}px` }}>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex flex-col">
                    <button className="text-muted-foreground hover:text-foreground" title="Subir"
                      onClick={() => mover(i, -1)}><ChevronUp className="h-3.5 w-3.5" /></button>
                    <button className="text-muted-foreground hover:text-foreground" title="Bajar"
                      onClick={() => mover(i, 1)}><ChevronDown className="h-3.5 w-3.5" /></button>
                  </div>
                  <button className="rounded p-1 text-muted-foreground hover:bg-muted" title="Menos sangría"
                    onClick={() => setFila(i, { nivel: Math.max(0, f.nivel - 1) })}><Outdent className="h-4 w-4" /></button>
                  <button className="rounded p-1 text-muted-foreground hover:bg-muted" title="Más sangría"
                    onClick={() => setFila(i, { nivel: Math.min(4, f.nivel + 1) })}><Indent className="h-4 w-4" /></button>

                  <input className={`${field} w-64 ${f.negrita ? 'font-semibold' : ''}`} value={f.etiqueta}
                    placeholder="Nombre de la fila" onChange={(e) => setFila(i, { etiqueta: e.target.value })} />
                  <select className={`${field} w-28`} value={f.clase}
                    onChange={(e) => setFila(i, { clase: e.target.value })}>
                    {CLASES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
                  </select>
                  <select className={`${field} w-28`} value={f.medida}
                    onChange={(e) => setFila(i, { medida: e.target.value as Medida })}
                    title="Flujo = movimiento del período · Saldo = acumulado al cierre">
                    <option value="flujo">Flujo</option>
                    <option value="saldo">Saldo</option>
                  </select>
                  <label className="flex items-center gap-1 text-xs text-muted-foreground">
                    <input type="checkbox" checked={f.negrita} onChange={(e) => setFila(i, { negrita: e.target.checked })} />
                    negrita
                  </label>

                  <span className="ml-auto min-w-[8rem] text-right text-sm tabular-nums">
                    {v == null ? '—' : <span className={v < 0 ? 'text-destructive' : ''}>{usd(v)}</span>}
                  </span>
                  {d != null && (
                    d === 0
                      ? <span title="Cuadra con la suma de sus filas hijas"><Check className="h-4 w-4 text-estable" /></span>
                      : <span className="inline-flex items-center gap-1 text-xs text-amber-600"
                          title="No cuadra con la suma de sus filas hijas">
                          <AlertTriangle className="h-4 w-4" /> {usd(d)}
                        </span>
                  )}
                  <button className="rounded p-1 text-muted-foreground hover:bg-muted" title="Insertar fila debajo"
                    onClick={() => agregarFila(i)}><Plus className="h-4 w-4" /></button>
                  <button className="rounded p-1 text-destructive hover:bg-destructive/10" title="Eliminar fila"
                    onClick={() => quitarFila(i)}><Trash2 className="h-4 w-4" /></button>
                </div>

                {f.clase !== 'separador' && (
                  <div className="mt-2 flex flex-wrap items-center gap-1">
                    {f.terminos.map((t, j) => (
                      <Chip key={`${t.tipo}-${t.valor}-${j}`} t={t} filas={filas} plan={plan}
                        invalida={refInvalida(i, t)}
                        onToggle={() => setFila(i, {
                          terminos: f.terminos.map((x, k) => (k === j ? { ...x, signo: x.signo === '+' ? '-' : '+' } : x)),
                        })}
                        onQuitar={() => setFila(i, { terminos: f.terminos.filter((_, k) => k !== j) })} />
                    ))}
                    <button className="inline-flex items-center gap-1 rounded-full border border-dashed px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted"
                      onClick={() => setAbierta(abierta === f.key ? null : f.key)}>
                      <Plus className="h-3 w-3" /> término
                    </button>
                    {f.terminos.length === 0 && (
                      <span className="text-xs text-muted-foreground">Sin términos: esta fila vale 0.</span>
                    )}
                  </div>
                )}

                {abierta === f.key && (
                  <AgregarTermino filas={filas} idx={i} plan={plan}
                    onAgregar={(t) => setFila(i, { terminos: [...f.terminos, t] })}
                    onCerrar={() => setAbierta(null)} />
                )}
              </div>
            );
          })}

          <div className="flex gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={() => set({ filas: [...filas, filaVacia()] })}>
              <Plus className="mr-1 h-4 w-4" /> Agregar fila
            </Button>
            <Button variant="outline" size="sm"
              onClick={() => {
                const u = filas[filas.length - 1];
                // copia profunda de los términos: si no, las dos filas compartirían el mismo arreglo
                set({ filas: [...filas, { ...u, key: nuevaKey(), terminos: u.terminos.map((t) => ({ ...t })) }] });
              }}
              disabled={filas.length === 0}>
              <Copy className="mr-1 h-4 w-4" /> Duplicar la última
            </Button>
          </div>
        </CardContent>
      </Card>

      <PanelCobertura filas={filas} desde={desde} hasta={hasta} />
    </div>
  );
}
